import { useEffect, useMemo, useState } from 'react';
import {
  GiftIcon, TruckIcon, BanknotesIcon, GlobeAltIcon, ShieldCheckIcon, TagIcon, LockClosedIcon,
} from '@heroicons/react/24/outline';
import { useMarket } from '../../context/MarketContext';
import { useOffers } from '../../context/OffersContext';

const ROTATE_MS = 4500;

/** One calm message at a time (it used to be a ticker of emojis), changing every few seconds; hover pauses it. */
const AnnouncementBar = () => {
  const { currentMarket } = useMarket();
  const { offers } = useOffers();
  const isIndia = currentMarket?.isDefault ?? true;
  const sym     = currentMarket?.currencySymbol ?? '₹';
  const fee     = currentMarket?.shippingBaseFee ?? 80;
  const thresh  = currentMarket?.freeShippingThreshold ?? 699;

  const ITEMS = useMemo(() => {
    const items = offers.slice(0, 2).map((o) => ({ Icon: GiftIcon, text: o.title }));   // current offers first
    if (isIndia) {
      const shippingText = thresh === 0
        ? `Fast Delivery · Free Shipping on All Prepaid Orders`
        : `Fast Delivery at ${sym}${fee} · Free Shipping on Prepaid ${sym}${thresh}+`;
      items.push({ Icon: TruckIcon, text: shippingText });
      items.push({ Icon: BanknotesIcon, text: 'Cash on Delivery Available Pan-India' });
    } else {
      const shippingText = thresh === 0
        ? 'Free Delivery on All Orders!'
        : `Free Shipping on orders above ${sym}${thresh}+`;
      items.push({ Icon: TruckIcon, text: shippingText });
      items.push({ Icon: GlobeAltIcon, text: 'International Delivery Available' });
    }
    items.push({ Icon: ShieldCheckIcon, text: '100% Authentic · Dermatologist Recommended' });
    items.push({ Icon: TagIcon, text: 'Use code SAVE10 · 10% off your first order' });
    items.push({ Icon: LockClosedIcon, text: 'Secured Shopping · 256-bit SSL Encryption' });
    return items;
  }, [isIndia, sym, fee, thresh, offers]);

  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (paused || ITEMS.length < 2) return undefined;
    const t = setInterval(() => setIndex((i) => (i + 1) % ITEMS.length), ROTATE_MS);
    return () => clearInterval(t);
  }, [paused, ITEMS.length]);

  const item = ITEMS[index % ITEMS.length];
  if (!item) return null;
  const { Icon, text } = item;

  return (
    <div
      className="bg-primary-dark text-white select-none"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      role="region"
      aria-label="Store announcements"
    >
      <div className="max-w-7xl mx-auto px-4 h-9 flex items-center justify-center">
        <p key={index} className="inline-flex items-center gap-2 text-[11px] sm:text-xs font-medium tracking-[0.06em] animate-fade-in truncate">
          <Icon className="w-3.5 h-3.5 shrink-0 text-white/80" aria-hidden="true" />
          <span className="truncate">{text}</span>
        </p>
      </div>
    </div>
  );
};

export default AnnouncementBar;
